import type { JournalKind, JournalListQuery } from '@sahab/contracts'
import { z } from 'zod'
import { isIsoDate } from '../../lib/dates'

export const JOURNAL_KINDS = [
  'MANUAL',
  'SYSTEM',
  'REVERSAL',
  'OPENING',
  'CLOSING',
] as const satisfies readonly JournalKind[]
export const JOURNAL_PAGE_SIZES = [25, 50, 100] as const

const isoDate = z
  .string()
  .refine((v) => isIsoDate(v))
  .optional()
  .catch(undefined)

/** The journal list lives in the URL; invalid values fall back to the default. */
export const journalSearchSchema = z.object({
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z
    .number()
    .refine((n): n is (typeof JOURNAL_PAGE_SIZES)[number] =>
      (JOURNAL_PAGE_SIZES as readonly number[]).includes(n),
    )
    .optional()
    .catch(undefined),
  from: isoDate,
  to: isoDate,
  kind: z.enum(JOURNAL_KINDS).optional().catch(undefined),
  accountId: z.uuid().optional().catch(undefined),
})
export type JournalSearch = z.infer<typeof journalSearchSchema>

export type JournalListParams = Omit<JournalListQuery, 'page' | 'pageSize'> & {
  page: number
  pageSize: number
}

export function toJournalListQuery(search: JournalSearch): JournalListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? JOURNAL_PAGE_SIZES[0],
    from: search.from,
    to: search.to,
    kind: search.kind,
    accountId: search.accountId,
  }
}

/** Trial balance period in the URL; defaults to this month (applied by the page). */
export const trialBalanceSearchSchema = z.object({ from: isoDate, to: isoDate })
export type TrialBalanceSearch = z.infer<typeof trialBalanceSearchSchema>
